import React, { useEffect, useState, useCallback } from "react"; // Ajout de useCallback
import { Box, Typography, CircularProgress, Stack, Paper, Alert, AlertTitle, Tooltip, IconButton } from "@mui/material";
import { Analytics, Refresh } from "@mui/icons-material";
import { fetchAPI } from "../../utils/api";
import { Modal, PayoutDashboard } from "../interfaces/PayoutDashboard";
import { useDispatch } from "react-redux";
import { hiddenLoader } from "../../stores/loading";
import { InvoiceModal } from "./InvoiceModal";
import { CurrencyAccordion } from "./CurrencyAccordion";
import { Invoice } from "../interfaces/Invoice";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PayoutSearchForm } from "./PayoutSearchForm";

export const PayoutDashboardTab = () => {
  const dispatch = useDispatch();
  const [payoutDashboard, setPayoutDashboard] = useState<PayoutDashboard>({} as PayoutDashboard);

  const [payoutLastSearchParam, setPayoutLastSearchParam] = useState<{ from: string, to: string, currency: string[] }>(() => {
    const saved = sessionStorage.getItem("payout_last_search_params");
    return saved ? JSON.parse(saved) : { from: "", to: "", currency: [] }
  });

  const [currencyOptions, setCurrencyOptions] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [modal, setModal] = useState<Modal>({ open: false, invoices: [], currency: "" });

  const search = useCallback(async (form: any, refresh: boolean) => {
    setIsSearching(true);
    const formattedFromDate = form.from + "T00:00";
    const formattedToDate = form.to + "T00:00";

    const refreshParam = refresh ? '&refresh=true' : '';
    const currencyParam = form.currency?.[0] || 'ALL';

    try {
      const res = await fetchAPI(`/invoices/payout?from=${formattedFromDate}&to=${formattedToDate}&currency=${currencyParam}${refreshParam}`);
      if (res.ok) {
        const data = await res.json();
        setPayoutDashboard(data);
        setPayoutLastSearchParam(form);
        sessionStorage.setItem("payout_last_search_params", JSON.stringify(form));
      };
    } finally {
      dispatch(hiddenLoader());
      setIsSearching(false);
    }
  }, [dispatch]);

  // GET CURRENCY and GET LAST SEARCH PARAM + CALL API
  useEffect(() => {
    fetchAPI("/operators/currency")
      .then((res) => res.json())
      .then(setCurrencyOptions);

    const savedParam = sessionStorage.getItem("payout_last_search_params");
    if (savedParam) {
      search(JSON.parse(savedParam), false);
    }
  }, [search]);

  const hasData = payoutDashboard?.dashboard && payoutDashboard.dashboard.length > 0;

  return (
    <Box sx={{ p: 4, bgcolor: '#f4f7f9', minHeight: '100vh' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a' }}>
          Payout Monitoring
        </Typography>

        <Tooltip title="Force refresh data">
          <span>
            <IconButton
              onClick={() => search(payoutLastSearchParam, true)}
              disabled={isSearching || !payoutLastSearchParam.from}
              sx={{
                bgcolor: 'white',
                boxShadow: 1,
                '&:hover': { bgcolor: '#e2e8f0' }
              }}
            >
              <Refresh sx={{ animation: isSearching ? 'spin 2s linear infinite' : 'none' }} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Alert
        severity="info"
        icon={<InfoOutlinedIcon fontSize="large" />}
        sx={{ mb: 4, borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}
      >
        <AlertTitle sx={{ fontWeight: 800 }}>Payout Monitoring Overview</AlertTitle>
        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
          This interface monitors the status of invoices and their readiness for payout.
          The date filter targets the <strong>invoice creation date</strong>.
          Note that some invoices may be generated the day before the actual payout for processing.
        </Typography>
      </Alert>

      <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        {currencyOptions.length > 0 && (
          <PayoutSearchForm
            currencyOptions={currencyOptions}
            search={search}
            defaultValues={payoutLastSearchParam}
          />
        )}
      </Paper>

      {isSearching ? (
        <Stack alignItems="center" sx={{ mt: 10 }}><CircularProgress /></Stack>
      ) : hasData ? (
        <>
          {[...payoutDashboard.dashboard]
            .sort((a, b) => a.currency === 'ALL' ? -1 : 1)
            .map((row) => (
              <CurrencyAccordion
                key={row.currency}
                row={row}
                onOpenInvoices={(invoices: Invoice[], curr: string) =>
                  setModal({ open: true, invoices, currency: curr })
                }
              />
            ))}

          <InvoiceModal
            open={modal.open}
            onClose={() => setModal({ ...modal, open: false })}
            invoices={modal.invoices}
            currency={modal.currency}
          />
        </>
      ) : (
        <Box sx={{ mt: 10, textAlign: 'center', opacity: 0.5 }}>
          <Analytics sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h6">No data to display. Please run a search.</Typography>
        </Box>
      )}
    </Box>
  );
};